import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import EmptyState from '@/components/feature/EmptyState';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { allStars, getHotKeywords, searchArticles } from '@/utils/articles';

type Tab = 'all' | 'gossip' | 'news' | 'star';

const tabs: { key: Tab; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'gossip', label: '黑料' },
  { key: 'news', label: '资讯' },
  { key: 'star', label: '明星专题' },
];

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const q = searchParams.get('q') || '';
  const [input, setInput] = useState(q);
  const [tab, setTab] = useState<Tab>('all');

  useDocumentMeta(
    q ? `搜索${q} - 91黑料` : '站内搜索 - 91黑料',
    q ? `91黑料站内搜索「${q}」的相关结果，涵盖娱乐黑料、娱乐资讯与明星专题。` : undefined,
  );

  useEffect(() => {
    setInput(q);
    setTab('all');
  }, [q]);

  const hotKeywords = getHotKeywords();

  const articleResults = useMemo(() => {
    if (!q) return [];
    const type = tab === 'gossip' ? 'gossip' : tab === 'news' ? 'news' : undefined;
    return searchArticles(q, type);
  }, [q, tab]);

  const starResults = useMemo(() => {
    if (!q || tab === 'gossip' || tab === 'news') return [];
    const kw = q.trim().toLowerCase();
    return allStars.filter(
      (s) => s.name.toLowerCase().includes(kw) || s.title.toLowerCase().includes(kw),
    );
  }, [q, tab]);

  const total = articleResults.length + (tab === 'star' ? starResults.length : 0);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const kw = input.trim();
    if (!kw) return;
    navigate(`/search?q=${encodeURIComponent(kw)}`);
  };

  return (
    <div className="max-w-content mx-auto px-4 md:px-6 py-6 md:py-8">
      <Breadcrumb items={[{ label: '站内搜索' }]} />

      <header className="mt-5 md:mt-6">
        <h1 className="text-2xl md:text-3xl font-black text-foreground-950 flex items-center gap-3">
          <span className="w-10 h-10 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center">
            <i className="ri-search-line text-xl"></i>
          </span>
          站内搜索
        </h1>

        <form onSubmit={submit} className="mt-5 flex flex-col sm:flex-row gap-3 max-w-2xl">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400"></i>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入关键词，搜索黑料、资讯或明星"
              className="w-full pl-10 pr-3 py-3 text-sm rounded-md bg-background-50 border border-background-300 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
            />
          </div>
          <button
            type="submit"
            className="px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            搜索
          </button>
        </form>
      </header>

      {!q ? (
        <div className="mt-10">
          <EmptyState
            icon="ri-search-eye-line"
            title="输入关键词开始搜索"
            description="支持搜索标题与正文中的关键词，快速找到你关心的娱乐黑料与资讯。"
          />
          <div className="max-w-2xl mx-auto -mt-8">
            <p className="text-sm text-foreground-500 mb-3 text-center">热门搜索</p>
            <div className="flex flex-wrap justify-center gap-2">
              {hotKeywords.map((kw) => (
                <button
                  key={kw}
                  type="button"
                  onClick={() => navigate(`/search?q=${encodeURIComponent(kw)}`)}
                  className="px-4 py-1.5 rounded-full bg-background-100 text-foreground-700 text-sm hover:bg-background-200 transition-colors whitespace-nowrap"
                >
                  {kw}
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="mt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <p className="text-sm text-foreground-600">
              搜索「<span className="font-bold text-primary-600">{q}</span>」共找到{' '}
              <span className="font-bold text-foreground-950">{total}</span> 条结果
            </p>
            <div className="flex items-center gap-1 px-1 py-1 bg-background-100 rounded-full w-fit">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`px-3 py-1 text-xs rounded-full whitespace-nowrap transition-colors ${
                    tab === t.key ? 'bg-primary-500 text-background-50' : 'text-foreground-600'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6 space-y-8">
            {starResults.length > 0 && (
              <section>
                <h2 className="text-sm font-bold text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-user-star-line text-accent-600"></i> 明星专题（{starResults.length}）
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {starResults.map((star) => (
                    <Link
                      key={star.id}
                      to={`/star/${star.id}`}
                      className="flex items-center gap-3 p-3 bg-background-50 border border-background-200 rounded-lg hover:border-primary-300 transition-colors"
                    >
                      <span className="w-12 h-12 rounded-full overflow-hidden bg-background-100 shrink-0">
                        <img
                          src={star.avatar}
                          alt={star.name}
                          title={`${star.name}-91黑料明星专题`}
                          className="w-full h-full object-cover object-top"
                        />
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-foreground-950">{star.name}</p>
                        <p className="text-xs text-foreground-500 truncate">{star.title}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {articleResults.length > 0 && (
              <section>
                <h2 className="text-sm font-bold text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-article-line text-primary-500"></i> 内容结果（{articleResults.length}）
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {articleResults.map((article) => (
                    <ArticleCard key={article.id} article={article} variant="horizontal" />
                  ))}
                </div>
              </section>
            )}

            {total === 0 && (
              <div>
                <EmptyState
                  icon="ri-emotion-sad-line"
                  title="未找到相关内容"
                  description={`没有找到与「${q}」相关的爆料或资讯，换个关键词或看看热门搜索吧。`}
                  actionTo="/"
                  actionLabel="返回首页"
                />
                <div className="max-w-2xl mx-auto -mt-10">
                  <p className="text-sm text-foreground-500 mb-3 text-center">换个关键词试试</p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {hotKeywords.map((kw) => (
                      <Link
                        key={kw}
                        to={`/search?q=${encodeURIComponent(kw)}`}
                        className="px-4 py-1.5 rounded-full bg-background-100 text-foreground-700 text-sm hover:bg-background-200 transition-colors whitespace-nowrap"
                      >
                        {kw}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      <p className="mt-10 text-xs text-foreground-400 flex items-center gap-1.5">
        <i className="ri-information-line"></i>
        搜索结果基于站内演示数据（前端模拟），仅用于功能展示。
      </p>
    </div>
  );
}